// Dependencies
import React, { PureComponent } from 'react';
import {
  View,
  FlatList,
  Text,
  TouchableOpacity,
  Image,
  ActivityIndicator,
} from 'react-native';
import PropTypes from 'prop-types';
import { max, min, map } from 'lodash';
import { LAYOUT, ORIENTATION, RemoveOutsideGridViewportComponents, SEP } from '../../../config/Constants';
import Utility from '../../../utils/Utility';
// Components
import LAYOUT_CONFIG from '../../../config/LayoutConstants/LayoutConfig';
import styles from './styles';
import { SCREEN_CONSTANTS } from '../../../config/ScreenConstants';
import Images from '../../../theme/Images';
import colors from '../../../theme/Colors';
import { getCardHeight, getGridComponent } from './GridHelper';
import GridStyles from './styles';
import { FlashList } from '@shopify/flash-list';
import ErrorBoundaryComponent from '../../shared/ErrorBoundaryComponent';
import { isDesktop, isIOS, isWeb, isAndroid } from '../../../utils/BooleanUtility';
import WebFooter from '../../webFooter/WebFooter';
import { getScreenHeight } from '../../../utils/LayoutUtility';

class Grid extends PureComponent {
  static getComponentHeight({
    objects = [],
    content,
    size,
    freeProductScreen,
    columns,
    display_count: displayCount,
    options = {},
  }) {
    const cardCount = objects.length;
    if (cardCount === 0) return 0;
    const cardType = objects[0].type;
    const rowCount =
      displayCount < cardCount ? displayCount / columns : cardCount / columns;
    return (
      getCardHeight(
        cardType,
        content,
        size,
        freeProductScreen,
        columns,
        options,
      ) * rowCount
    );
  }

  constructor(props) {
    super(props);
    const {
      item,
      content,
      size,
      freeProductScreen,
      displayCount,
      avoidFlashList,
    } = props;
    this.displayCount = max([LAYOUT_CONFIG.minListCount, displayCount || 0]);
    this.itemData = item.filter((item) => !!item) || [];
    this.column = LAYOUT_CONFIG.numColumn[content] || 2;
    this.viewedItemsIds = [];
    this.state = {
      hideActivityIndicator: false,
    };
    const { sku: { itemSkuType = '' } = {}, type: itemType = '' } =
      this.itemData[0] || {};
    const cardType = itemSkuType || itemType;
    this.ContainerComponent = getGridComponent(
      cardType,
      content,
      size,
      freeProductScreen,
    );
    this.cardHeight = getCardHeight(
      cardType,
      content,
      size,
      freeProductScreen,
      this.column,
    );

    // Calculate viewport-based values
    const screenHeight = getScreenHeight() - 125; // Total screen height
    this.numColumns = isDesktop() ? this.column * 2 : this.column;
    const visibleRows = Math.ceil(screenHeight / this.cardHeight); // Number of rows that fit in the viewport
    const visibleItemCount = Math.max(visibleRows * this.numColumns, 4); // Total visible items, minimum 4
    this.initialNumToRender = visibleItemCount - 2;
    this.windowSize = Math.max(visibleRows * this.numColumns * 2, 5); // Double the visible items for buffer, minimum 5
    this.maxToRenderPerBatch = Math.max(visibleRows * this.numColumns, 7); // Match visible items, minimum 7
  }

  getComponent = ({ item, index }) => {
    if (Utility.isBlank(item)) {
      return null;
    }
    const {
      type,
      size,
      id,
      content,
      navigation,
      toggleCartVisibility,
      listName,
      listIndex,
      previousScreen,
      listData,
      listData: { slug: listSlug } = {},
      refreshOfferStrip,
      maxFreeItemsToSelect,
      showToast,
      fromPersonalisedCard,
      updateSelectedItem,
      selectedItem,
      viewFreeProductScreen,
      listContent,
      onPress,
      searchQuery,
      elementItemCounts,
      freeProductScreen,
      search,
      display,
      paginateLastItem,
      unLike,
      showEditButton,
      removeFromList,
      foxyPoints,
      brandId = '',
      categoryIds = [],
      extraEventParameters = {},
      itemData,
      hideOosProduct = false,
      selectedFilters,
    } = this.props;
    if (content === 'sku' || content === 'product') {
      this.ContainerComponent = getGridComponent(
        item.type,
        content,
        size,
        freeProductScreen,
      );
    }
    if (item === undefined || this.ContainerComponent === undefined) {
      return null;
    }
    const ContainerComponent = this.ContainerComponent;
    return (
      <ErrorBoundaryComponent
        itemData={item}
        listData={listData}
        screenName={previousScreen}
      >
        <ContainerComponent
          id={item.id}
          layout={LAYOUT.GRID}
          itemData={item}
          listName={listName}
          listContent={listContent}
          type={item.type}
          size={size}
          listIndex={listIndex}
          listSlug={listSlug}
          navigation={navigation}
          orientation={ORIENTATION.VERTICAL}
          listId={id}
          index={index}
          toggleCartVisibility={toggleCartVisibility}
          previousScreen={previousScreen}
          listData={listData}
          refreshOfferStrip={refreshOfferStrip}
          maxFreeItemsToSelect={maxFreeItemsToSelect}
          showToast={showToast}
          personalisedGrid={false}
          onPress={onPress}
          search={search}
          searchQuery={searchQuery}
          elementItemCounts={elementItemCounts}
          content={content}
          updateSelectedItem={updateSelectedItem}
          selectedItem={selectedItem}
          viewFreeProductScreen={viewFreeProductScreen}
          freeProductScreen={freeProductScreen}
          display={display}
          hideActivityIndicator={this.hideActivityIndicator}
          paginateLastItem={paginateLastItem}
          showEditButton={showEditButton}
          removeFromList={removeFromList}
          foxyPoints={foxyPoints}
          brandId={brandId}
          categoryIds={categoryIds}
          extraEventParameters={extraEventParameters}
          hideOosProduct={hideOosProduct}
          selectedFilters={selectedFilters}
        />
      </ErrorBoundaryComponent>
    );
  };

  refetchFeed = () => {
    const { refetchFeed } = this.props;
    refetchFeed();
  };

  renderFooter = () => {
    // it will show indicator at the bottom of the list when data is loading otherwise it returns null
    const {
      loadMore = false,
      feedLoadFailed,
      previousScreen,
      showFooterInList,
    } = this.props;

    const { hideActivityIndicator } = this.state;
    if (previousScreen === 'search') {
      return null;
    }

    if (feedLoadFailed) {
      return (
        <View style={styles.feedFooterContainer}>
          <TouchableOpacity onPress={() => this.refetchFeed()}>
            <View style={styles.footerTextAndImageContainer}>
              <Text style={styles.footerButtonText}>Load More</Text>
              <Image
                style={styles.footerChevronImage}
                source={Images.chevronDown}
              />
            </View>
          </TouchableOpacity>
        </View>
      );
    }

    // console.tron.log('grid indicat', loadMore, !hideActivityIndicator);

    if (loadMore) {
      const activityIndicatorStyle =
        previousScreen === SCREEN_CONSTANTS.CATEGORY_BROWSING
          ? GridStyles.verticalTabsActivityIndicatorStyle
          : GridStyles.activityIndicatorStyle;

      return (
        <ActivityIndicator
          size='large'
          style={activityIndicatorStyle}
          color={colors.foxyPink}
        />
      );
    }

    if (!loadMore && showFooterInList) {
      return <WebFooter />;
    }

    return null;
  };

  hideActivityIndicator = () => {
    this.setState({ hideActivityIndicator: true });
  };

  keyExtractor = (item) => `${item.id}_grid`;

  listKey = (item, index) => `Grid$${item.id}`;

  getItemLayout = (item, index) => {
    return { length: this.cardHeight, offset: this.cardHeight * index, index };
  };

  onEndReached = () => {
    const { endReached = () => {} } = this.props;
    endReached();
  };

  renderEmptySearch = () => {
    const { previousScreen, listData, verticalListHeader, avoidFlashList } =
      this.props;

    if (Utility.isBlank(this.itemData)) {
      return null;
    }

    const columns =
      previousScreen !== SCREEN_CONSTANTS.MORE_PAGE &&
      !Utility.isBlank(listData) &&
      !Utility.isBlank(listData.display) &&
      listData.display === 'grid' &&
      Utility.isPresent(listData.columns)
        ? listData.columns
        : this.column;

    const applyKeyExtractor =
      avoidFlashList || isWeb() ? this.keyExtractor : null;
    const initialNumToRender = isDesktop() ? 20 : 10;
    const windowSize = isDesktop() ? 42 : 21;
    const maxToRenderPerBatch = isDesktop() ? 20 : 10;
    return (
      <FlashList
        data={this.itemData}
        numColumns={isDesktop() ? columns * 2 : columns}
        extraData={this.props}
        showsVerticalScrollIndicator={false}
        keyExtractor={applyKeyExtractor}
        listKey={this.listKey} // added to prevent virtuaized list crash
        renderItem={this.getComponent}
        style={GridStyles.flatlistStyle}
        contentContainerStyle={styles.contentContainerStyle}
        initialNumToRender={initialNumToRender}
        windowSize={windowSize}
        maxToRenderPerBatch={maxToRenderPerBatch}
        ListFooterComponent={this.renderFooter}
        ListEmptyComponent={this.emptyState}
        ListHeaderComponent={verticalListHeader}
        onEndReached={this.onEndReached}
        estimatedItemSize={this.cardHeight}
        removeClippedSubviews={isIOS()}
      />
    );
  };

  render() {
    const {
      previousScreen,
      listData,
      paginationResult,
      isSearchResultScreen,
      productAndBrandsOnlySwitchValue,
      content,
      search,
      filtersEnabled,
      onPressResetButton,
      verticalListHeader,
      showEditButton,
      editableList,
      avoidFlashList,
    } = this.props;
    // if (previousScreen === SCREEN_CONSTANTS.EMPTY_SEARCH) return <this.renderEmptySearch />;
    if (Utility.isBlank(this.itemData) && Utility.isPresent(listData.objects)) {
      this.itemData = listData.objects;
    }

    if (Utility.isBlank(this.itemData)) {
      return null;
    }

    this.displayCount = max([
      LAYOUT_CONFIG.minGridCount,
      this.props.displayCount || 0,
    ]);
    this.itemData = this.props.item.filter((item) => !!item);
    if (this.displayCount > this.itemData.length) {
      this.displayCount = this.itemData.length;
    }

    if (
      previousScreen === SCREEN_CONSTANTS.SEARCH &&
      listData.content === 'product' &&
      !Utility.isBlank(paginationResult)
    ) {
      this.itemData = this.itemData.concat(Object.values(paginationResult));
    }

    if (
      previousScreen === SCREEN_CONSTANTS.SEARCH &&
      listData.content !== 'product' &&
      productAndBrandsOnlySwitchValue
    ) {
      this.displayCount = 2;
    }

    if (
      isSearchResultScreen &&
      productAndBrandsOnlySwitchValue &&
      listData.content === 'product'
    ) {
      this.displayCount = this.itemData.length;
    }

    const columns =
      previousScreen !== SCREEN_CONSTANTS.MORE_PAGE &&
      !Utility.isBlank(listData) &&
      !Utility.isBlank(listData.display) &&
      listData.display === 'grid' &&
      Utility.isPresent(listData.columns)
        ? listData.columns
        : this.column;

    const data =
      previousScreen === SCREEN_CONSTANTS.MORE_PAGE
        ? this.itemData
        : this.itemData.slice(0, this.displayCount);

    const dataToRender = showEditButton
      ? editableList
      : map(data, (e) => {
          return { ...e, key: e.id };
        });
    const getItemLayoutFn = search ? undefined : this.getItemLayout;

    const RenderComponent =
      avoidFlashList || (isWeb() && previousScreen !== SCREEN_CONSTANTS.CATEGORY_BROWSING) || isIOS() ? FlatList : FlashList;
    this.displayCount = Math.min(max([LAYOUT_CONFIG.minGridCount, this.props.displayCount || 0]), 50);
    const applyKeyExtractor = avoidFlashList || (isWeb() && previousScreen !== SCREEN_CONSTANTS.CATEGORY_BROWSING) || isIOS() ? this.keyExtractor : null;
    // In Android, we need to provide an initial number for rendering, which is usually half the length of the render items. However, in iOS, this isn’t required. If we apply the same Android approach in iOS, it could negatively impact the back button performance.
    const initialNumToRender = isAndroid()
      ? min([Math.round(dataToRender?.length / 2 || 1), 50])
      : this.initialNumToRender;
    return (
      <RenderComponent
        data={dataToRender}
        numColumns={isDesktop() ? columns * 2 : columns}
        scrollEnabled
        extraData={this.props}
        showsVerticalScrollIndicator={false}
        keyExtractor={applyKeyExtractor}
        listKey={this.listKey}
        renderItem={this.getComponent}
        style={GridStyles.flatlistStyle}
        contentContainerStyle={styles.contentContainerStyle}
        initialNumToRender={initialNumToRender} // Use calculated value
        windowSize={this.windowSize}
        maxToRenderPerBatch={this.maxToRenderPerBatch}
        ListFooterComponent={this.renderFooter}
        ListEmptyComponent={this.emptyState}
        getItemLayout={getItemLayoutFn}
        ListHeaderComponent={verticalListHeader}
        onEndReached={this.onEndReached}
        estimatedItemSize={this.cardHeight || 200}
      />
    );
  }
}

Grid.propTypes = {
  list: PropTypes.shape({
    objects: PropTypes.array,
    size: PropTypes.string,
  }),
  item: PropTypes.array,
  content: PropTypes.string,
};

export default Grid;
